// ============================================================
// BUSINESS DATE MANAGER
// ============================================================
// Centralised business date for all engines. Supports overriding
// the system date for simulation, testing, and EOD replay.
//
// Usage:
//   import { businessDate, BusinessDateManager } from './business-date.js';
//   businessDate.getCurrentDate();           // Date object
//   businessDate.setDate(new Date('2025-06-15'));
//   businessDate.advanceDay();
//   businessDate.reset();                    // back to system clock
// ============================================================

// UK bank holidays 2025–2026 (England & Wales)
const UK_BANK_HOLIDAYS = new Set([
  // 2025
  '2025-01-01', // New Year's Day
  '2025-04-18', // Good Friday
  '2025-04-21', // Easter Monday
  '2025-05-05', // Early May bank holiday
  '2025-05-26', // Spring bank holiday
  '2025-08-25', // Summer bank holiday
  '2025-12-25', // Christmas Day
  '2025-12-26', // Boxing Day

  // 2026
  '2026-01-01', // New Year's Day
  '2026-04-03', // Good Friday
  '2026-04-06', // Easter Monday
  '2026-05-04', // Early May bank holiday
  '2026-05-25', // Spring bank holiday
  '2026-08-31', // Summer bank holiday
  '2026-12-25', // Christmas Day
  '2026-12-28', // Boxing Day (substitute — 26th is Saturday)
]);

class BusinessDateManager {
  constructor() {
    this._override = null;
  }

  getCurrentDate() {
    if (this._override) {
      return new Date(this._override.getTime());
    }
    return new Date();
  }

  setDate(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error('setDate requires a valid Date object');
    }
    this._override = new Date(date.getTime());
  }

  advanceDay() {
    const d = this.getCurrentDate();
    d.setUTCDate(d.getUTCDate() + 1);
    this._override = d;
    return this.getCurrentDate();
  }

  advanceDays(n) {
    if (!Number.isFinite(n) || n < 0) throw new Error('advanceDays requires a non-negative number');
    const d = this.getCurrentDate();
    d.setUTCDate(d.getUTCDate() + n);
    this._override = d;
    return this.getCurrentDate();
  }

  advanceToMonthEnd() {
    const d = this.getCurrentDate();
    // Move to last day of current month
    d.setUTCMonth(d.getUTCMonth() + 1, 0);
    this._override = d;
    return this.getCurrentDate();
  }

  reset() {
    this._override = null;
  }

  isBusinessDay(date) {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) throw new Error('isBusinessDay requires a valid date');
    const day = d.getUTCDay();
    if (day === 0 || day === 6) return false; // Weekend
    const dateStr = d.toISOString().split('T')[0];
    if (UK_BANK_HOLIDAYS.has(dateStr)) return false;
    return true;
  }

  nextBusinessDay(date) {
    const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
    if (isNaN(d.getTime())) throw new Error('nextBusinessDay requires a valid date');
    d.setUTCDate(d.getUTCDate() + 1);
    while (!this.isBusinessDay(d)) {
      d.setUTCDate(d.getUTCDate() + 1);
    }
    return d;
  }
}

// Singleton instance — shared across all engines by default
const businessDate = new BusinessDateManager();

export { BusinessDateManager, businessDate, UK_BANK_HOLIDAYS };
